import { Button, FileUploadIcon, Paragraph, notification } from "@datagame/ui";
import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { SelectInput } from "../../../../shared/ui/SelectInput";
import { LibraryImageKind } from "../../../../../gql/graphql";
import { CREATE_LIBRARY_IMAGE } from "../../../../../graphql/mutations/LibraryImageMutations";
import { LIBRARY_IMAGES_QUERY } from "../../../../../graphql/queries/LibraryImageQueries";
import { SelectOption } from "../../../../shared/ui/SelectInput/SelectInput";
import { FileDropzone } from "../../../../shared/FileDropzone";
import { Section } from "../../../../project/ProjectView/ProjectSettings/Steps/Common";

import styles from "./UploadForm.module.scss";

export interface UploadFormProps {
  onUpload?: () => void;
}

export const UploadForm = ({ onUpload }: UploadFormProps) => {
  const { t } = useTranslation();

  const categories = useMemo(() => {
    return [
      {
        value: LibraryImageKind.Background,
        label: "Background",
      },
      {
        value: LibraryImageKind.CardFront,
        label: "Card Front",
      },
      {
        value: LibraryImageKind.CardBack,
        label: "Card Back",
      },
      {
        value: LibraryImageKind.Logo,
        label: "Logo",
      },
      {
        value: LibraryImageKind.Poster,
        label: "Poster",
      },
    ];
  }, []);

  const [category, setCategory] = useState(categories.find((item) => item.value === LibraryImageKind.Background));
  const [selectedImages, setSelectedImages] = useState<File[] | null>(null);
  const [uploadImage] = useMutation(CREATE_LIBRARY_IMAGE, { refetchQueries: [LIBRARY_IMAGES_QUERY] });

  const handleCategoryChange = (item: SelectOption) => {
    const newValue = categories.find((option) => {
      return option.value === item.value;
    });
    setCategory(newValue);
  };

  const handleUpload = () => {
    if (!selectedImages || !category) return;
    uploadImage({
      variables: {
        input: {
          files: selectedImages,
          kind: category.value,
        },
      },
      onCompleted: () => {
        notification.info({
          title: t("library:notification.title", { count: selectedImages.length }),
          description: t("library:notification.description", { count: selectedImages.length }),
        });
        setSelectedImages(null);
        onUpload?.();
      },
    });
  };

  return (
    <div className={styles.container}>
      <Section title={t("library:uploadSection.selectCategory")} className={styles.categorySection}>
        <SelectInput options={categories} onChange={handleCategoryChange} selected={category} />
      </Section>

      {selectedImages &&
        selectedImages.map((selectedImage) => (
          <div key={selectedImage.name} className={styles.uploadedImageSection}>
            <img className={styles.image} src={URL.createObjectURL(selectedImage)} alt="Upload" />
            <Paragraph className={styles.imageName} weight="font-bold">
              {selectedImage.name}
            </Paragraph>
          </div>
        ))}

      <div className={styles.dropzoneSection}>
        <FileDropzone maxFiles={5} onFileUpload={(files) => setSelectedImages(files)} />
      </div>

      <Button icon={<FileUploadIcon />} iconSide="left" disabled={!selectedImages} onClick={handleUpload}>
        {t("library:uploadSection.upload")}
      </Button>
    </div>
  );
};
